import { useEffect, useRef } from 'react';

const ParticleBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Particle class
    class Particle {
      x: number;
      y: number;
      size: number;
      speedX: number;
      speedY: number;
      opacity: number;

      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 1;
        this.speedX = (Math.random() - 0.5) * 0.3; // Reduced speed
        this.speedY = (Math.random() - 0.5) * 0.3; // Reduced speed
        this.opacity = Math.random() * 0.4 + 0.3; // Reduced opacity variation
      }

      update() {
        // Attraction to mouse - DISABLED for performance
        // if (distance < 150) { ... }
        
        this.x += this.speedX;
        this.y += this.speedY;

        if (this.x > canvas.width) this.x = 0;
        if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        if (this.y < 0) this.y = canvas.height;
      }

      draw() {
        if (!ctx) return;
        // Enhanced brand colors (more vibrant)
        const colors = [
          `rgba(0, 220, 255, ${this.opacity})`,     // brighter cyan
          `rgba(160, 110, 255, ${this.opacity})`,   // more vibrant purple
          `rgba(255, 50, 170, ${this.opacity})`     // more saturated pink
        ];
        const colorIndex = Math.floor(this.x / canvas.width * 3) % 3;
        ctx.fillStyle = colors[colorIndex];
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Create particles - significantly reduced for performance
    const isMobile = canvas.width < 768;
    const particleCount = isMobile ? 40 : 80; // Reduced from 80/200 to 40/80
    const particles: Particle[] = [];
    
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    // Mouse interaction - DISABLED for performance
    let mouseX = -1000;
    let mouseY = -1000;
    
    // Removed mousemove listener for better performance
    // Only draw particles, don't compute connections

    // Skip particle connections for significant performance gain
    const connectParticles = () => {
      // Particle connection lines disabled to improve performance
      // The visual effect is maintained through opacity and movement
    };

    // Animation loop with frame skip optimization
    let frameCount = 0;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });
      
      connectParticles();
      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none opacity-50"
      style={{ mixBlendMode: 'screen' }}
    />
  );
};

export default ParticleBackground;
